import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { ArrowDown, ArrowUp, Search, Users, ChevronLeft, ChevronRight, Trash2 } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { deleteItem, deleteCandidate, plural, TrashButton } from "@/lib/deleteItem";
import { motion } from "framer-motion";
import { ProvisionalTag } from "@/components/fit/Provisional";
import { formatDistanceToNow } from "date-fns";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/lib/auth";
import { get } from "@/lib/api";
import { EmptyState, SkeletonRows } from "@/components/app/Common";
import { StatusChip } from "@/components/app/StatusChip";
import { parseChip, SOURCE_LABELS, STAGE_LABELS } from "@/lib/candidates";
import { INTAKE_SOURCES } from "@/lib/intake";
import { ConsentBadge } from "@/components/intake/ConsentBadge";

const COLS = [["name", "Name"], [null, "Email"], [null, "Source"], ["parse_status", "Parse status"], [null, "Stage"], [null, "Fit Score"], ["last_activity", "Last activity"]];

function SortHeader({ col, label, sort, order, onSort }) {
  if (!col) return <th className="px-4 py-3 font-medium">{label}</th>;
  const active = sort === col;
  return (
    <th className="px-4 py-3 font-medium">
      <button onClick={() => onSort(col)} className="inline-flex items-center gap-1 hover:text-ink" data-testid={`sort-${col}`}>
        {label}{active && (order === "asc" ? <ArrowUp size={12} /> : <ArrowDown size={12} />)}
      </button>
    </th>
  );
}

export function CandidatesTable({ jobId, status, showJobs, emptyAction, testId = "candidates-table" }) {
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("last_activity");
  const [order, setOrder] = useState("desc");
  const [page, setPage] = useState(1);
  const { user } = useAuth();
  const isAdmin = user?.role === "super_admin";
  const [showDeleted, setShowDeleted] = useState(false);
  const [source, setSource] = useState("");
  const pageSize = 25;
  const { data, isLoading } = useQuery({
    queryKey: ["candidates", jobId, status, q, sort, order, page, showDeleted, source],
    queryFn: () => get("/candidates", { job_id: jobId, status, q, sort, order, page, page_size: pageSize, include_deleted: showDeleted || undefined, source: source || undefined }),
    placeholderData: keepPreviousData,
    refetchInterval: 5000,
  });
  const onSort = (col) => {
    if (col === sort) setOrder(order === "asc" ? "desc" : "asc");
    else { setSort(col); setOrder(col === "name" ? "asc" : "desc"); }
    setPage(1);
  };
  const pages = data ? Math.max(1, Math.ceil(data.total / pageSize)) : 1;
  const qc = useQueryClient();
  const staff = ["super_admin", "recruiter"].includes(user?.role);
  const [sel, setSel] = useState(new Set());
  const toggle = (id) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const items = data?.items || [];
  const allOn = items.length > 0 && items.every((c) => sel.has(c.id));
  const drop = (ids) => {
    qc.setQueriesData({ queryKey: ["candidates"] }, (old) => (old?.items ? { ...old, items: old.items.filter((c) => !ids.includes(c.id)), total: old.total - ids.length } : old));
    setSel((s) => new Set([...s].filter((x) => !ids.includes(x))));
    qc.invalidateQueries({ queryKey: ["candidates"] }); qc.invalidateQueries({ queryKey: ["jobs"] });
  };
  const bulk = () => {
    const ids = [...sel];
    deleteItem({ title: `Delete ${plural(ids.length, "candidate")} entirely?`, typeToConfirm: "DELETE", method: "post", url: "/candidates/bulk-delete", data: { ids },
      body: "This also removes all their applications, assessment invites, interviews, notes, scores and resume files. This can't be undone.",
      success: `Deleted ${plural(ids.length, "candidate")}`, onDone: () => drop(ids) });
  };

  return (
    <div className="space-y-3" data-testid={testId}>
      <div className="flex flex-wrap items-center gap-4">
      <div className="relative w-full max-w-xs">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted" />
        <input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="Filter by name"
          className="h-9 w-full rounded-[5px] border border-line bg-white pl-8 pr-3 text-sm outline-none focus:border-brand/40" data-testid={`${testId}-filter`} />
      </div>
      <select value={source} onChange={(e) => { setSource(e.target.value); setPage(1); }} aria-label="Filter by source" data-testid={`${testId}-source-filter`}
        className="h-9 rounded-[5px] border border-line bg-white px-2 text-sm outline-none focus:border-brand/40">
        <option value="">All sources</option>
        {INTAKE_SOURCES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
      {isAdmin && <label className="flex items-center gap-2 text-sm text-ink-secondary"><Switch checked={showDeleted} onCheckedChange={(v) => { setShowDeleted(v); setPage(1); }} data-testid={`${testId}-show-deleted`} /> Show deleted</label>}
      {staff && sel.size > 0 && <Button variant="outline" size="sm" className="ml-auto text-risk hover:text-risk" onClick={bulk} data-testid={`${testId}-bulk-delete`}><Trash2 size={14} /> Delete selected ({sel.size})</Button>}
      </div>
      {isLoading ? <SkeletonRows rows={6} /> : !data.items.length ? (
        <EmptyState testId={`${testId}-empty`} icon={Users} title={q ? "No candidates match" : "No candidates yet"}
          text={q ? "Try a different name." : "Add resumes to this job or share the careers page to start receiving applications."} action={!q && emptyAction} />
      ) : (
        <div className="panel overflow-hidden max-lg:overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-subtle text-left text-xs text-ink-muted">
              <tr>
                {staff && <th className="w-10 pl-4"><Checkbox checked={allOn} onCheckedChange={(v) => setSel(v ? new Set(items.map((c) => c.id)) : new Set())} aria-label="Select all candidates" data-testid={`${testId}-select-all`} /></th>}
                {COLS.map(([col, label]) => <SortHeader key={label} col={col} label={label} sort={sort} order={order} onSort={onSort} />)}
                {showJobs && <th className="px-4 py-3 font-medium">Jobs</th>}
                <th className="w-8"><span className="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {data.items.map((c) => {
                const open = () => nav(`/app/candidates/${c.id}/resume`, { state: { name: c.display_name } });
                return (
                <tr key={c.id} onClick={open} onKeyDown={(e) => e.key === "Enter" && open()} tabIndex={0} className="tl-row" data-testid={`candidate-row-${c.id}`}>
                  {staff && <td className="w-10 pl-4" onClick={(e) => e.stopPropagation()}><Checkbox checked={sel.has(c.id)} onCheckedChange={() => toggle(c.id)} aria-label={`Select ${c.display_name}`} data-testid={`candidate-select-${c.id}`} /></td>}
                  <td className="px-4 py-3 font-medium text-ink"><motion.span layoutId={`cand-name-${c.id}`} className="inline-block">{c.display_name}</motion.span>{c.deleted && <span className="ml-2 rounded-full bg-risk-tint px-2 py-0.5 text-[10px] text-risk">Deleted</span>}{c.consent?.privacy_notice_pending && <span className="ml-2 inline-block align-middle"><ConsentBadge consent={c.consent} testId={`candidate-consent-${c.id}`} /></span>}</td>
                  <td className="px-4 py-3 text-ink-secondary" data-testid={`candidate-email-${c.id}`}>{c.email || "—"}</td>
                  <td className="px-4 py-3 text-ink-secondary" data-testid={`candidate-source-${c.id}`}>{SOURCE_LABELS[c.source] || c.source}</td>
                  <td className="px-4 py-3"><StatusChip status={parseChip(c)} /></td>
                  <td className="px-4 py-3 text-ink-secondary">{c.stage ? STAGE_LABELS[c.stage] || c.stage : "—"}</td>
                  <td className="px-4 py-3 font-display font-semibold tabular-nums" data-testid={`candidate-fit-${c.id}`}>{c.fit_score != null ? <>{Number(c.fit_score).toFixed(1)}{c.fit_provisional && <ProvisionalTag testId={`candidate-fit-provisional-${c.id}`} />}</> : <span className="font-sans font-normal text-ink-muted">—</span>}</td>
                  <td className="px-4 py-3 text-xs text-ink-muted">{formatDistanceToNow(new Date(c.last_activity_at), { addSuffix: true })}</td>
                  {showJobs && <td className="px-4 py-3 text-xs text-ink-secondary">{Object.values(c.jobs || {}).join(", ") || "Talent pool"}</td>}
                  <td className="whitespace-nowrap pr-3 text-right">{staff && <TrashButton label={`Delete ${c.display_name}`} testId={`delete-candidate-${c.id}`} onClick={() => deleteCandidate({ id: c.id, name: c.display_name }, () => drop([c.id]))} />}<ChevronRight size={16} className="tl-chev inline text-ink-muted" aria-hidden="true" /></td>
                </tr>
                );
              })}
            </tbody>
          </table>
          <div className="flex items-center justify-between border-t border-line px-4 py-2 text-xs text-ink-muted">
            <span data-testid={`${testId}-total`}>{data.total} candidate{data.total === 1 ? "" : "s"}</span>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid={`${testId}-prev`}><ChevronLeft size={14} /></Button>
              <span>Page {page} of {pages}</span>
              <Button variant="ghost" size="sm" disabled={page >= pages} onClick={() => setPage(page + 1)} data-testid={`${testId}-next`}><ChevronRight size={14} /></Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
